import { useEffect, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  type CommissionDetails,
  type CommissionEditProposedChanges,
  type CommissionTransaction,
} from "@/lib/commission.api";
import { DatePicker } from "@/components/ui/date-picker";
import { useRequestCommissionEditMutation } from "@/hooks/mutations/useRequestCommissionEdit";
import { useCommissionTypes } from "@/hooks/queries/useCommissionTypes";
import { useCommissionParties } from "@/hooks/queries/useCommissionParties";
import { CommissionDynamicFields } from "@/components/customs/CommissionDynamicFields";
import { PartySelect } from "@/pages/documents/PartySelect";
import type { Party } from "@/lib/party.api";

interface RequestCommissionEditDialogProps {
  commission: CommissionTransaction;
  onRequested: () => void;
}

function sameJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? {}) === JSON.stringify(right ?? {});
}

export function RequestCommissionEditDialog({
  commission,
  onRequested,
}: RequestCommissionEditDialogProps) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(commission.date.split("T")[0]);
  const [amount, setAmount] = useState(parseFloat(commission.commissionAmount));
  const [client, setClient] = useState<Party | null>(null);
  const [referrer, setReferrer] = useState<Party | null>(null);
  const [details, setDetails] = useState<CommissionDetails>(commission.details ?? {});
  const [note, setNote] = useState(commission.note ?? "");
  const [reason, setReason] = useState("");
  const requestMutation = useRequestCommissionEditMutation();
  const { data: types = [] } = useCommissionTypes();
  const { data: parties = {} } = useCommissionParties([commission]);

  const selectedType = types.find((type) => type.code === commission.type);

  function reset() {
    setDate(commission.date.split("T")[0]);
    setAmount(parseFloat(commission.commissionAmount));
    setClient(commission.clientPartyId ? (parties[commission.clientPartyId] ?? null) : null);
    setReferrer(commission.referrerPartyId ? (parties[commission.referrerPartyId] ?? null) : null);
    setDetails(commission.details ?? {});
    setNote(commission.note ?? "");
    setReason("");
  }

  useEffect(() => {
    if (!open) return;
    if (commission.clientPartyId && !client && parties[commission.clientPartyId]) {
      setClient(parties[commission.clientPartyId]);
    }
    if (commission.referrerPartyId && !referrer && parties[commission.referrerPartyId]) {
      setReferrer(parties[commission.referrerPartyId]);
    }
  }, [client, commission.clientPartyId, commission.referrerPartyId, open, parties, referrer]);

  function handleSubmit() {
    const proposedChanges: CommissionEditProposedChanges = {};
    if (date !== commission.date.split("T")[0]) proposedChanges.date = date;
    if (amount !== parseFloat(commission.commissionAmount)) proposedChanges.commissionAmount = amount;
    if ((client?.id ?? null) !== (commission.clientPartyId ?? null)) {
      proposedChanges.clientPartyId = client?.id ?? null;
    }
    if ((referrer?.id ?? null) !== (commission.referrerPartyId ?? null)) {
      proposedChanges.referrerPartyId = referrer?.id ?? null;
    }
    if (!sameJson(details, commission.details ?? {})) proposedChanges.details = details;
    if (note !== (commission.note ?? "")) proposedChanges.note = note || null;

    if (Object.keys(proposedChanges).length === 0) {
      toast.error("Aucun changement détecté.");
      return;
    }

    requestMutation.mutate(
      { commissionId: commission.id, reason, proposedChanges },
      {
        onSuccess: () => {
          setOpen(false);
          reset();
          onRequested();
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        reset();
      }}
    >
      <DialogTrigger>
        <Button variant="ghost" size="icon" title="Demander une modification">
          <Pencil size={13} />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Demander une modification</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-[11px] text-muted-foreground">
            Cette demande sera soumise à validation. La commission ne changera qu'après
            approbation par un administrateur.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <DatePicker value={date} onChange={setDate} />
            </div>
            <div>
              <Label>Montant</Label>
              <Input
                type="number"
                value={amount}
                onChange={(event) => setAmount(parseFloat(event.target.value) || 0)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client (optionnel)</Label>
              <PartySelect value={client} onChange={setClient} placeholder="Rechercher un client..." />
            </div>
            <div>
              <Label>Référent (optionnel)</Label>
              <PartySelect
                value={referrer}
                onChange={setReferrer}
                filterReferrer
                placeholder="Rechercher un référent..."
              />
            </div>
          </div>

          {selectedType?.fieldSchema && (
            <CommissionDynamicFields
              fieldSchema={selectedType.fieldSchema}
              values={details}
              onChange={setDetails}
            />
          )}

          <div>
            <Label>Note</Label>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={2}
              className="flex w-full resize-none rounded border border-border bg-card px-3 py-2 text-sm"
            />
          </div>

          <div>
            <Label>Raison de la modification (obligatoire)</Label>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              placeholder="Pourquoi ce changement est-il nécessaire ?"
              className="flex w-full resize-none rounded border border-border bg-card px-3 py-2 text-sm"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={requestMutation.isPending || !reason.trim()}>
            {requestMutation.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              "Soumettre la demande"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
